import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reusable StatCard component
const StatCard = ({ title, value, subtitle, icon, color = "blue", trend }) => (
  <div className={`bg-white rounded-xl p-4 shadow-sm border border-${color}-100 hover:shadow-md transition`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className={`text-2xl font-bold text-${color}-600`}>{value?.toLocaleString() || 0}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`text-3xl bg-${color}-50 p-3 rounded-xl`}>{icon}</div>
    </div>
    {trend && (
      <div className={`mt-2 text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
        {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}% vs last period
      </div>
    )}
  </div>
);

// Mini Chart Component
const MiniChart = ({ data, color = "#3B82F6" }) => {
  if (!data || data.length === 0) return <div className="h-16 flex items-center justify-center text-gray-400 text-sm">No data</div>;
  
  const max = Math.max(...data.map(d => d.value || 0), 1);
  const width = 100 / data.length;
  
  return (
    <div className="h-16 flex items-end gap-1">
      {data.slice(-14).map((d, i) => (
        <div
          key={i}
          className="flex-1 rounded-t transition-all hover:opacity-80"
          style={{
            height: `${((d.value || 0) / max) * 100}%`,
            backgroundColor: color,
            minHeight: d.value > 0 ? '4px' : '2px'
          }}
          title={`${d.label || d.date}: ${d.value}`}
        />
      ))}
    </div>
  );
};

// Lead Funnel Component
const LeadFunnel = ({ data }) => {
  const stages = data?.funnel_stages || [];
  const maxCount = Math.max(...stages.map(s => s.count || 0), 1);
  
  return (
    <div className="space-y-3">
      {stages.map((stage, idx) => {
        const width = Math.max((stage.count / maxCount) * 100, 10);
        const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-orange-500', 'bg-purple-500'];
        return (
          <div key={idx} className="flex items-center gap-3">
            <span className="text-2xl w-8">{stage.icon}</span>
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium">{stage.stage}</span>
                <span className="text-gray-600">{stage.count?.toLocaleString()}</span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${colors[idx % colors.length]} rounded-full transition-all duration-500`}
                  style={{ width: `${width}%` }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Main Analytics Dashboard Component
export default function AnalyticsDashboard({ token }) {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [engagementData, setEngagementData] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [followUpsData, setFollowUpsData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [periodDays, setPeriodDays] = useState(30);

  useEffect(() => {
    loadAllData();
  }, [periodDays]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [dashboard, funnel, engagement, team, geo, trends, followUps] = await Promise.all([
        fetch(`${API_URL}/api/school-finder/analytics/dashboard-summary`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/school-finder/analytics/lead-funnel?days=${periodDays}`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/school-finder/analytics/engagement?days=${periodDays}`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/school-finder/analytics/team-performance?days=${periodDays}`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/school-finder/analytics/geographic?days=${periodDays}`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/school-finder/analytics/trends?days=${periodDays}`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/school-finder/follow-ups/dashboard`, { headers }).then(r => r.json())
      ]);
      
      setDashboardData(dashboard);
      setFunnelData(funnel);
      setEngagementData(engagement);
      setTeamData(team);
      setGeoData(geo);
      setTrendsData(trends);
      setFollowUpsData(followUps);
    } catch (err) {
      console.error('Error loading analytics:', err);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{language === 'hi' ? 'Analytics लोड हो रहा है...' : 'Loading Analytics...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            📊 {language === 'hi' ? 'Analytics Dashboard' : 'Analytics Dashboard'}
          </h1>
          <p className="text-gray-500 text-sm">
            {language === 'hi' ? 'School outreach और sales performance' : 'School outreach & sales performance'}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <select 
            value={periodDays} 
            onChange={(e) => setPeriodDays(parseInt(e.target.value))}
            className="px-3 py-2 border rounded-lg text-sm"
            data-testid="period-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button 
            onClick={loadAllData}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
            data-testid="refresh-btn"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'overview', label: language === 'hi' ? 'Overview' : 'Overview', icon: '📊' },
          { id: 'funnel', label: language === 'hi' ? 'Lead Funnel' : 'Lead Funnel', icon: '🎯' },
          { id: 'team', label: language === 'hi' ? 'Team' : 'Team', icon: '👥' },
          { id: 'geo', label: language === 'hi' ? 'Geographic' : 'Geographic', icon: '🗺️' },
          { id: 'followups', label: language === 'hi' ? 'Follow-ups' : 'Follow-ups', icon: '⏰' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition ${
              activeTab === tab.id 
                ? 'bg-blue-500 text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard 
              title={language === 'hi' ? 'कुल Schools' : 'Total Schools'} 
              value={dashboardData?.quick_stats?.total_schools} 
              icon="🏫" 
              color="blue"
            />
            <StatCard 
              title="WhatsApp Ready" 
              value={dashboardData?.quick_stats?.with_whatsapp} 
              subtitle={`${dashboardData?.rates?.whatsapp_coverage}% coverage`}
              icon="💬" 
              color="green"
            />
            <StatCard 
              title="Map Ready" 
              value={dashboardData?.quick_stats?.map_ready} 
              subtitle={`${dashboardData?.rates?.map_coverage}% coverage`}
              icon="📍" 
              color="purple"
            />
            <StatCard 
              title="Converted" 
              value={dashboardData?.quick_stats?.converted} 
              subtitle={`${dashboardData?.rates?.conversion_rate}% rate`}
              icon="✅" 
              color="orange"
            />
            <StatCard 
              title={language === 'hi' ? 'Pending Follow-ups' : 'Pending Follow-ups'} 
              value={dashboardData?.quick_stats?.pending_followups} 
              icon="⏰" 
              color="red"
            />
          </div>

          {/* Activity Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold text-gray-700 mb-3">📅 {language === 'hi' ? 'आज की Activity' : "Today's Activity"}</h3>
              <p className="text-4xl font-bold text-blue-600">{dashboardData?.activity?.today_proposals || 0}</p>
              <p className="text-sm text-gray-500">{language === 'hi' ? 'Proposals भेजे' : 'Proposals sent'}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold text-gray-700 mb-3">📆 {language === 'hi' ? 'इस Week' : 'This Week'}</h3>
              <p className="text-4xl font-bold text-green-600">{dashboardData?.activity?.week_proposals || 0}</p>
              <p className="text-sm text-gray-500">{language === 'hi' ? 'Proposals भेजे' : 'Proposals sent'}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold text-gray-700 mb-3">📊 {language === 'hi' ? 'इस Month' : 'This Month'}</h3>
              <p className="text-4xl font-bold text-purple-600">{dashboardData?.activity?.month_proposals || 0}</p>
              <p className="text-sm text-gray-500">{language === 'hi' ? 'Proposals भेजे' : 'Proposals sent'}</p>
            </div>
          </div>

          {/* Trends Chart */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h3 className="font-semibold text-gray-700 mb-4">📈 {language === 'hi' ? 'Daily Trend' : 'Daily Trend'}</h3>
            <MiniChart data={trendsData?.data} color="#3B82F6" />
            <div className="flex justify-between text-xs text-gray-400 mt-2">
              <span>{trendsData?.data?.[0]?.label || ''}</span>
              <span>Total: {trendsData?.stats?.total || 0}</span>
              <span>{trendsData?.data?.[trendsData?.data?.length - 1]?.label || ''}</span>
            </div>
          </div>
        </div>
      )}

      {/* Lead Funnel Tab */}
      {activeTab === 'funnel' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-gray-700 mb-4">🎯 Lead Funnel</h3>
            <LeadFunnel data={funnelData} />
          </div>
          
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-gray-700 mb-4">📊 Conversion Rates</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span>Contact Rate</span>
                <span className="font-bold text-blue-600">{funnelData?.rates?.contact_rate || 0}%</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span>Demo Rate</span>
                <span className="font-bold text-green-600">{funnelData?.rates?.demo_rate || 0}%</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                <span>Conversion Rate</span>
                <span className="font-bold text-purple-600">{funnelData?.rates?.conversion_rate || 0}%</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                <span>Overall Conversion</span>
                <span className="font-bold text-orange-600">{funnelData?.rates?.overall_conversion || 0}%</span>
              </div>
            </div>
          </div>

          {/* Channel Breakdown */}
          <div className="bg-white rounded-xl p-6 shadow-sm md:col-span-2">
            <h3 className="font-semibold text-gray-700 mb-4">📤 Channel Breakdown</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {engagementData?.channel_breakdown?.map((ch, idx) => (
                <div key={idx} className="text-center p-4 rounded-lg" style={{ backgroundColor: `${ch.color}15` }}>
                  <span className="text-3xl">{ch.icon}</span>
                  <p className="font-bold text-xl mt-2" style={{ color: ch.color }}>{ch.count}</p>
                  <p className="text-sm text-gray-600">{ch.channel}</p>
                </div>
              ))}
              <div className="text-center p-4 rounded-lg bg-gray-50">
                <span className="text-3xl">📊</span>
                <p className="font-bold text-xl mt-2">{engagementData?.engagement?.response_rate || 0}%</p>
                <p className="text-sm text-gray-600">Response Rate</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-blue-50">
                <span className="text-3xl">📈</span>
                <p className="font-bold text-xl mt-2 text-blue-600">{engagementData?.engagement?.avg_daily_proposals || 0}</p>
                <p className="text-sm text-gray-600">Avg Daily</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Team Performance Tab */}
      {activeTab === 'team' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard title="Team Members" value={teamData?.team_summary?.total_members} icon="👥" color="blue" />
            <StatCard title="Total Proposals" value={teamData?.team_summary?.total_proposals} icon="📤" color="green" />
            <StatCard title="Total Responses" value={teamData?.team_summary?.total_responses} icon="📥" color="purple" />
            <StatCard title="Avg Per Member" value={teamData?.team_summary?.avg_per_member} icon="📊" color="orange" />
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-gray-700 mb-4">🏆 Leaderboard</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-500 border-b">
                    <th className="pb-3 pr-4">#</th>
                    <th className="pb-3 pr-4">Name</th>
                    <th className="pb-3 pr-4 text-center">Proposals</th>
                    <th className="pb-3 pr-4 text-center">WhatsApp</th>
                    <th className="pb-3 pr-4 text-center">Email</th>
                    <th className="pb-3 pr-4 text-center">Responses</th>
                    <th className="pb-3 text-center">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {teamData?.leaderboard?.map((member, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="py-3 pr-4">
                        {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                      </td>
                      <td className="py-3 pr-4 font-medium">{member.name || 'Unknown'}</td>
                      <td className="py-3 pr-4 text-center font-bold text-blue-600">{member.proposals_sent}</td>
                      <td className="py-3 pr-4 text-center text-green-600">{member.whatsapp_count}</td>
                      <td className="py-3 pr-4 text-center text-purple-600">{member.email_count}</td>
                      <td className="py-3 pr-4 text-center">{member.responses}</td>
                      <td className="py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${member.response_rate > 20 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {member.response_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Geographic Tab */}
      {activeTab === 'geo' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-gray-700 mb-4">🗺️ State-wise Performance</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-500 border-b">
                    <th className="pb-3 pr-4">State</th>
                    <th className="pb-3 pr-4 text-center">Total Schools</th>
                    <th className="pb-3 pr-4 text-center">With Contact</th>
                    <th className="pb-3 pr-4 text-center">Contacted</th>
                    <th className="pb-3 pr-4 text-center">Converted</th>
                    <th className="pb-3 text-center">Conv. Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {geoData?.state_breakdown?.slice(0, 15).map((state, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="py-3 pr-4 font-medium">{state.state}</td>
                      <td className="py-3 pr-4 text-center">{state.total_schools?.toLocaleString()}</td>
                      <td className="py-3 pr-4 text-center text-blue-600">{state.with_contact?.toLocaleString()}</td>
                      <td className="py-3 pr-4 text-center text-green-600">{state.contacted?.toLocaleString()}</td>
                      <td className="py-3 pr-4 text-center text-purple-600">{state.converted?.toLocaleString()}</td>
                      <td className="py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${state.conversion_rate > 10 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {state.conversion_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-gray-700 mb-4">⭐ Top Performing States</h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {geoData?.top_performing_states?.map((state, idx) => (
                <div key={idx} className="text-center p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl">
                  <p className="text-2xl mb-2">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '🏅'}</p>
                  <p className="font-bold text-gray-800">{state.state}</p>
                  <p className="text-sm text-purple-600">{state.converted} converted</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Follow-ups Tab */}
      {activeTab === 'followups' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard 
              title={language === 'hi' ? 'Pending' : 'Pending'} 
              value={followUpsData?.summary?.total_pending} 
              icon="⏳" 
              color="blue"
            />
            <StatCard 
              title="Overdue" 
              value={followUpsData?.summary?.overdue} 
              icon="⚠️" 
              color="red"
            />
            <StatCard 
              title={language === 'hi' ? 'आज के Due' : 'Due Today'} 
              value={followUpsData?.summary?.today_due} 
              icon="📅" 
              color="orange"
            />
            <StatCard 
              title={language === 'hi' ? 'इस Week Complete' : 'Completed This Week'} 
              value={followUpsData?.summary?.completed_this_week} 
              icon="✅" 
              color="green"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-700 mb-4">🎯 By Priority</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <span className="flex items-center gap-2">🔥 Hot Leads</span>
                  <span className="font-bold text-red-600">{followUpsData?.by_priority?.hot || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <span className="flex items-center gap-2">🌡️ Warm Leads</span>
                  <span className="font-bold text-orange-600">{followUpsData?.by_priority?.warm || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <span className="flex items-center gap-2">❄️ Cold Leads</span>
                  <span className="font-bold text-blue-600">{followUpsData?.by_priority?.cold || 0}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-700 mb-4">📊 Response Rate</h3>
              <div className="text-center py-8">
                <p className="text-5xl font-bold text-green-600">{followUpsData?.summary?.response_rate || 0}%</p>
                <p className="text-gray-500 mt-2">{language === 'hi' ? 'इस Week का Response Rate' : "This Week's Response Rate"}</p>
              </div>
            </div>
          </div>

          {/* Alerts */}
          {followUpsData?.alerts?.filter(a => a).length > 0 && (
            <div className="space-y-2">
              {followUpsData.alerts.filter(a => a).map((alert, idx) => (
                <div key={idx} className={`p-4 rounded-lg flex items-center gap-3 ${
                  alert.type === 'danger' ? 'bg-red-50 text-red-700' :
                  alert.type === 'warning' ? 'bg-yellow-50 text-yellow-700' :
                  'bg-blue-50 text-blue-700'
                }`}>
                  <span className="text-xl">{alert.type === 'danger' ? '🚨' : alert.type === 'warning' ? '⚠️' : 'ℹ️'}</span>
                  <span>{alert.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
